/**
 * IOT-HAL-1: Normalize vendor-specific timestamps to ISO 8601 UTC strings.
 *
 * Adapters call this at the boundary before constructing NormalizedReading.
 * Upstream consumers always see ISO 8601. Never pass vendor-native timestamps
 * to EventBus or Firestore.
 */

/**
 * Parse and normalize a vendor timestamp to ISO 8601 UTC string.
 *
 * Handles:
 *   - ISO 8601 strings (returned as-is after UTC normalization)
 *   - Unix epoch seconds (integer, ≤ 9999999999)
 *   - Unix epoch milliseconds (integer, > 9999999999)
 *   - Date objects
 *   - Vendor-specific RFC 2822 / non-standard date strings
 *
 * Returns null for unparseable inputs — caller sets qualityFlag='invalid'.
 *
 * @param {string | number | Date | null | undefined} raw
 * @returns {string | null} ISO 8601 UTC string or null
 */
export function normalizeTimestamp(raw) {
  if (raw == null) return null;

  if (raw instanceof Date) {
    return isNaN(raw.getTime()) ? null : raw.toISOString();
  }

  if (typeof raw === 'number') {
    // Epoch seconds vs milliseconds heuristic: 10-digit = seconds, 13-digit = ms
    const ms = raw <= 9_999_999_999 ? raw * 1000 : raw;
    const d = new Date(ms);
    return isNaN(d.getTime()) ? null : d.toISOString();
  }

  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    const d = new Date(trimmed);
    return isNaN(d.getTime()) ? null : d.toISOString();
  }

  return null;
}

/**
 * Quality-flag a timestamp based on its age.
 *
 * @param {string} isoTimestamp  ISO 8601 UTC
 * @param {{ staleThresholdMs?: number }} [opts]
 * @returns {'ok' | 'stale'}
 */
export function timestampQualityFlag(isoTimestamp, { staleThresholdMs = 5 * 60 * 1000 } = {}) {
  const ageMs = Date.now() - new Date(isoTimestamp).getTime();
  return ageMs > staleThresholdMs ? 'stale' : 'ok';
}
